import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { isNativePlatform } from '@lib/platform';
import {
  isPwaInstalled,
  getWebPlatform,
  canInstallPwa,
  getDeferredInstallPrompt,
  clearDeferredInstallPrompt,
  getDesktopBrowser,
} from '@lib/pwa/install-utils';
import type { BeforeInstallPromptEvent, DesktopBrowser } from '@lib/pwa/install-utils';
import { createGrantFlow } from '@lib/overlay/grantFlow';
import { InstallPromptDrawer } from '@components/InstallPromptDrawer';
import { defineStoredValue, FINITE_NUMBER } from '@utils/storedValue';
import { logger } from '@utils/logger';
import { InstallPromptContext, type InstallPromptContextValue } from './InstallPromptContext';

// ── Constants ──

const dismissedAtStore = defineStoredValue({
  storage: 'local',
  key: 'pwa_install_dismissed',
  ...FINITE_NUMBER,
});
const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

/**
 * The install drawer's turn on the overlay mutex. Module scope, so a queued
 * request survives re-renders; the drawer's open-state is derived from it and
 * nothing else writes it (overlay-system-design §6.3, §6.6).
 */
const installPromptFlow = createGrantFlow('install-prompt');

// ── Cooldown helpers ──

function isDismissCooldownActive(): boolean {
  const dismissedAt = dismissedAtStore.read();
  return dismissedAt !== null && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
}

const setCooldown = (): void => {
  dismissedAtStore.write(Date.now());
};

// ── Inert value for native platform (avoids conditional hook calls) ──

// eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional no-ops for native platform
const noop = () => {};
const asyncNoop = () => Promise.resolve();

const INERT_VALUE: InstallPromptContextValue = {
  isOpen: false,
  shouldPrompt: false,
  isInstalling: false,
  isInstalled: false,
  hasDeferredPrompt: false,
  platform: 'desktop',
  canInstall: false,
  show: noop,
  forceShow: noop,
  handleInstall: asyncNoop,
  handleDismiss: noop,
};

// ── Provider ──

export function InstallPromptProvider({ children }: { children: ReactNode }) {
  // On native, provide inert value and skip all browser-only logic
  if (isNativePlatform()) {
    return (
      <InstallPromptContext.Provider value={INERT_VALUE}>{children}</InstallPromptContext.Provider>
    );
  }

  return <WebInstallPromptProvider>{children}</WebInstallPromptProvider>;
}

function WebInstallPromptProvider({ children }: { children: ReactNode }) {
  const isOpen = useSyncExternalStore(
    installPromptFlow.subscribe,
    () => installPromptFlow.getState() === 'held',
  );
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInstalled, setIsInstalled] = useState(() => isPwaInstalled());
  const [hasDeferredPrompt, setHasDeferredPrompt] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  // Capture early event from index.html on mount
  useEffect(() => {
    const earlyEvent = getDeferredInstallPrompt();
    if (earlyEvent) {
      deferredPromptRef.current = earlyEvent;
      setHasDeferredPrompt(true);
      clearDeferredInstallPrompt();
    }

    // Listen for subsequent beforeinstallprompt events
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setHasDeferredPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // Reactive installed state — listen to matchMedia and appinstalled
  useEffect(() => {
    const mql = window.matchMedia('(display-mode: standalone)');
    const handleDisplayChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
      }
    };
    mql.addEventListener('change', handleDisplayChange);

    const handleAppInstalled = () => {
      setIsInstalled(true);
      installPromptFlow.end('programmatic');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mql.removeEventListener('change', handleDisplayChange);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // An unmounted host must never keep a grant with nothing on screen.
  useEffect(
    () => () => {
      installPromptFlow.end('programmatic');
    },
    [],
  );

  const platform = useMemo(() => getWebPlatform(), []);

  const canInstall = useMemo(() => {
    return !isInstalled && canInstallPwa();
  }, [isInstalled]);

  const shouldPrompt = useMemo(() => {
    if (isInstalled) return false;
    if (isOpen) return false;
    if (!canInstallPwa()) return false;
    if (isDismissCooldownActive()) return false;
    return true;
  }, [isInstalled, isOpen]);

  // Written when the user taps "Not Now", when the iOS/desktop instructions view's
  // "Got it" is tapped, or when handleInstall's native dialog is declined or errors;
  // never when handleInstall closes without a native prompt, or on unmount.
  const handleDismiss = useCallback(() => {
    installPromptFlow.end('user');
    setCooldown();
  }, []);

  const show = useCallback(() => {
    if (!shouldPrompt) return;
    installPromptFlow.request({
      stillNeeded: () => !isPwaInstalled() && !isDismissCooldownActive(),
    });
  }, [shouldPrompt]);

  // Skips the cooldown (Settings → "Install App"), but still takes its turn.
  const forceShow = useCallback(() => {
    if (isInstalled) return;
    if (!canInstallPwa()) return;
    installPromptFlow.request({
      stillNeeded: () => !isPwaInstalled(),
    });
  }, [isInstalled]);

  const handleInstall = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) {
      // No native prompt available (iOS, or already used) — just close
      installPromptFlow.end('programmatic');
      return;
    }

    setIsInstalling(true);
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        dismissedAtStore.clear();
      } else {
        setCooldown();
      }
    } catch (error) {
      logger.error('PWA install: prompt() failed:', error);
      setCooldown();
    } finally {
      deferredPromptRef.current = null;
      setHasDeferredPrompt(false);
      setIsInstalling(false);
      installPromptFlow.end('programmatic');
    }
  }, []);

  const value = useMemo<InstallPromptContextValue>(
    () => ({
      isOpen,
      shouldPrompt,
      isInstalling,
      isInstalled,
      hasDeferredPrompt,
      platform,
      canInstall,
      show,
      forceShow,
      handleInstall,
      handleDismiss,
    }),
    [
      isOpen,
      shouldPrompt,
      isInstalling,
      isInstalled,
      hasDeferredPrompt,
      platform,
      canInstall,
      show,
      forceShow,
      handleInstall,
      handleDismiss,
    ],
  );

  const desktopBrowser = useMemo<DesktopBrowser | undefined>(
    () => (platform === 'desktop' ? getDesktopBrowser() : undefined),
    [platform],
  );

  return (
    <InstallPromptContext.Provider value={value}>
      {children}
      <InstallPromptDrawer prompt={value} desktopBrowser={desktopBrowser} />
    </InstallPromptContext.Provider>
  );
}
